import React, { Fragment, useEffect, useState } from "react";
import Container from "@/components/container/Container";
import CardComponent from "@/components/card";
import { Col, Form, Input, Row, Select, DatePicker, Divider, Flex } from "antd";
import ButtonComponent from "@/components/button/Button";
import BackWithLabel from "@/components/common/BackWithLabel";
import IconAdd from "@/components/icons/IconAdd";
import IconDelete from "@/components/icons/IconDelete";
import IconCross from "@/components/icons/IconCross";
import { countryList } from "@/helpers/countryList";
import { getLevels } from "@/services/common/getLevels";
import { getRegionByCountry } from "@/services/common/getRegionByCountry";
import { getUniversityByCountryAndRegion } from "@/services/common/getUniversityByCountryAndRegion";
import { getDisciplines } from "@/services/common/getDisciplines";
import "@/styles/pages/profile/advance-carrier-information.scss";
import { addExperienceDetails } from "@/services/users/addExperienceDetails";
import { getExperienceDetails } from "@/services/users/getExperienceDetails";
import { updateExperienceDetails } from "@/services/users/updateExperienceDetails";
import { addEducationDetails } from "@/services/users/addEducationDetails";
import { getUserEducationDetails } from "@/services/users/getUserEducationDetails";
import { updateEducationDetails } from "@/services/users/updateEducationDetails";
import useTriggerNotification from "@/hooks/useTriggerNotification";
import useUnsavedChanges from "@/hooks/useUnsavedChanges";
import dayjs from "dayjs";

const bullionOptions = [
  {
    value: true,
    label: "Yes",
  },
  {
    value: false,
    label: "No",
  },
];

const formItemLayout = {
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 24 },
    md: { span: 24 },
    lg: { span: 24 },
    xl: { span: 24 },
  },
};

const formItemLayoutWithOutLabel = {
  wrapperCol: {
    xs: { span: 24, offset: 0 },
    sm: { span: 24, offset: 0 },
    md: { span: 24, offset: 0 },
    lg: { span: 24, offset: 0 },
    xl: { span: 24, offset: 0 },
  },
};

const customizeRequiredMark = (label, { required }) => (
  <>
    {label}
    {required && <span style={{ color: "red" }}>*</span>}
  </>
);

const filterOption = (input, option) => {
  return (option?.label ?? "").toLowerCase().includes(input.toLowerCase());
};

const { Option } = Select;
const { RangePicker } = DatePicker;

const AdvanceCarrierInformation = () => {
  const [isFormDisabled, setIsFormDisabled] = useState(true);
  const [levelOptions, setLevelOptions] = useState([]);
  const [regionOptions, setRegionOption] = useState({});
  const [universityOptions, setUniverityOption] = useState({});
  const [disciplineOption, setDisciplineOptions] = useState([]);

  const [selectedCountry, setSelectedCountry] = useState();
  const [selectedRegion, setSelectedRegion] = useState({});

  const [formData, setFormData] = useState({});

  const [form] = Form.useForm();
  const { trigger, element } = useTriggerNotification();

  useEffect(() => {
    fetchLevels();
    fetchDiscipline();
  }, []);

  const tagRender = (props) => {
    const { label, value, closable, onClose } = props;
    const onPreventMouseDown = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };

    return label?.length ? (
      <div
        className={isFormDisabled ? "disabled selected-chip" : "selected-chip"}
        onMouseDown={onPreventMouseDown}
      >
        <span>{label}</span>{" "}
        <IconCross
          style={{ cursor: isFormDisabled ? "not-allowed" : "pointer" }}
          color={isFormDisabled ? "#6d6e75" : "#db5800"}
          onClick={onClose}
        />
      </div>
    ) : (
      <></>
    );
  };
  // useEffect(() => {
  //   if (Object.keys(selectedCountry).length) {
  //     fetchRegionData();
  //   }
  // }, [selectedCountry]);

  // useEffect(() => {
  //   fetchUniversity();
  // }, [selectedRegion]);

  const fetchLevels = () => {
    getLevels().then((response) => {
      setLevelOptions(response?.map((x) => ({ label: x.name, value: x.id })));
    });
  };

  const fetchRegionData = (index, country) => {
    getRegionByCountry(country)
      .then((response) => {
        const regionData = response?.map((x) => ({
          label: x.name,
          value: x.id,
        }));
        regionData.push({ label: "Other", value: 0 });
        setRegionOption((prevstate) => ({ ...prevstate, [index]: regionData }));
        setUniverityOption((prevstate) => ({ ...prevstate, [index]: [] }));
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const fetchUniversity = (country, region, index) => {
    if (region == 0) {
      setUniverityOption((prevstate) => ({
        ...prevstate,
        [index]: [{ label: "Other", value: 0 }],
      }));
    } else if (country && region) {
      getUniversityByCountryAndRegion(country, region)
        .then((response) => {
          const universityData = response?.map((x) => ({
            label: x.name,
            value: x.id,
          }));
          universityData.push({ label: "Other", value: 0 });
          setUniverityOption((prevstate) => ({
            ...prevstate,
            [index]: universityData,
          }));
        })
        .catch((error) => console.log(error));
    }
  };

  const fetchDiscipline = () => {
    getDisciplines().then((response) => {
      setDisciplineOptions(response?.map((x) => ({ label: x.name, value: x.id })));
    });
  };

  const handleCountryOnChange = (index, value) => {
    setSelectedCountry((prevstate) => ({ ...prevstate, [index]: value }));
    fetchRegionData(index, value);
  };

  const handleRegionOnChange = (index, value, country = "") => {
    setSelectedRegion((prevstate) => ({ ...prevstate, [index]: value }));
    const countryCode = selectedCountry?.[index] ?? country;
    fetchUniversity(countryCode, value, index);
  };

  const handleFinish = (values) => {
    const primaryEducation = [
      {
        countryCode: values.countryCode,
        regionId: values.regionId,
        location: values.location,
        levelId: values.levelId,
        universityId: values.universityId,
        institutionName: values.institutionName,
        disciplineId: values.disciplineId,
        courseName: values.courseName,
        startDate: values.startDate?.format("YYYY"),
        endDate: values.endDate?.format("YYYY"),
        result: values.resultGrade,
        id: values.educationId,
      },
    ];

    const educations = values?.educations?.map((x, index) => {
      return {
        countryCode: x.countryCode,
        regionId: x.regionId,
        location: x.location,
        levelId: x.levelId,
        universityId: x.universityId,
        institutionName: x.institutionName,
        disciplineId: x.disciplineId,
        courseName: x.courseName,
        startDate: x.startDate?.format("YYYY"),
        endDate: x.endDate?.format("YYYY"),
        result: x.resultGrade,
        id: x?.[`educationId${index}`],
      };
    });
    const finalEducation = [...primaryEducation, ...(educations || [])];
    const educationToAdd = finalEducation.filter((x) => !x.id);
    const educationToUpdate = finalEducation.filter((x) => !!x.id);

    // Create an array of all promises
    const promises = [];

    // Add education promises
    educationToAdd.forEach((x) => {
      promises.push(addEducationDetails(x));
    });
    educationToUpdate.forEach((x) => {
      promises.push(updateEducationDetails(x.id, x));
    });

    // Add internship promises
    const primaryIntershipDetails = values.internshipExperience
      ? [
          {
            title: values.internshipName,
            institutionName: values.internshipCompany,
            institutionAddress: "",
            startDate: values.internshipStartDate?.[0]?.format("YYYY"),
            endDate: values.internshipStartDate?.[1]?.format("YYYY"),
            tag: "Internship",
            id: values.internshipId,
          },
        ]
      : [];
    const internships = values.internships?.map((internship, index) => ({
      title: internship.internshipName,
      institutionName: internship.internshipCompany,
      institutionAddress: "",
      startDate: internship.internshipStartDate?.[0]?.format("YYYY"),
      endDate: internship.internshipStartDate?.[1]?.format("YYYY"),
      tag: "Internship",
      id: internship[`internship${index}`],
    }));

    const primaryWorkDetails = !!values.workExperience
      ? [
          {
            title: values.jobTitle,
            institutionName: values.workCompany,
            institutionAddress: "",
            startDate: values.workStartDate?.[0]?.format("YYYY"),
            endDate: values.workStartDate?.[1]?.format("YYYY"),
            tag: "FullTime",
            id: values.workId,
          },
        ]
      : [];

    const works = values.works?.map((work, index) => ({
      title: work.jobTitle,
      institutionName: work.workCompany,
      institutionAddress: "",
      startDate: work.workStartDate?.[0]?.format("YYYY"),
      endDate: work.workStartDate?.[1]?.format("YYYY"),
      tag: "FullTime",
      id: work[`work${index}`],
    }));

    const finalData = [
      ...primaryIntershipDetails,
      ...(internships || []),
      ...primaryWorkDetails,
      ...(works || []),
    ];

    const dataToUpdate = finalData.filter((x) => !!x.id);
    const dataToAdd = finalData.filter((x) => !x.id);

    dataToAdd.forEach((x) => {
      promises.push(addExperienceDetails(x));
    });
    dataToUpdate.forEach((x) => {
      promises.push(updateExperienceDetails(x.id, x));
    });

    // Execute all promises and handle the response
    Promise.all(promises)
      .then(() => {
        // Fetch latest data and update form
        fetchData();
        trigger({
          type: "success",
          message: "Career and Education updated successfully",
        });
        setIsFormDisabled(true);
      })
      .catch((error) => {
        console.error("Error updating data:", error);
        trigger({
          type: "error",
          message: "Failed to update Career and Education",
        });
      });
  };

  const fetchData = () => {
    getUserEducationDetails()
      .then((res) => {
        if (res.length) {
          const primaryEducation = res.slice(0, 1);
          fetchRegionData(0, primaryEducation[0].countryCode);
          handleRegionOnChange(
            0,
            primaryEducation[0].region?.id ?? 0,
            primaryEducation[0].countryCode
          );
          setSelectedCountry((prevstate) => ({
            ...prevstate,
            0: primaryEducation[0].countryCode,
          }));
          const primaryEducationDetails = {
            countryCode: primaryEducation[0].countryCode,
            regionId: primaryEducation[0].region?.id ?? 0,
            location: primaryEducation[0].location,
            levelId: primaryEducation[0].level?.id,
            universityId: primaryEducation[0].university?.id ?? 0,
            institutionName: primaryEducation[0].institutionName,
            disciplineId: [primaryEducation[0].discipline?.id ?? 0],
            courseName: primaryEducation[0].courseName,
            startDate: dayjs(primaryEducation[0].startDate, "YYYY"),
            endDate: dayjs(primaryEducation[0].endDate, "YYYY"),
            resultGrade: primaryEducation[0].result,
            educationId: primaryEducation[0].id,
          };

          const educations = res?.slice(1).map((x, index) => ({
            countryCode: x.countryCode,
            regionId: x.region?.id ?? 0,
            location: x.location,
            levelId: x.level?.id,
            universityId: x.university?.id ?? 0,
            institutionName: x.institutionName,
            disciplineId: [x.discipline?.id ?? 0],
            courseName: x.courseName,
            startDate: dayjs(x.startDate, "YYYY"),
            endDate: dayjs(x.endDate, "YYYY"),
            resultGrade: x.result,
            [`educationId${index}`]: x.id,
          }));

          form.setFieldsValue({
            ...primaryEducationDetails,
            educations: educations,
          });
        }
      })
      .catch((error) => console.log(error));

    getExperienceDetails().then((res) => {
      const intershipDetails = res.filter((x) => x.tag === "internship");
      const workDetails = res.filter((x) => x.tag === "full-time");
      setFormData({
        workExperience: workDetails?.length > 0,
        internshipExperience: intershipDetails?.length > 0,
      });
      form.setFieldsValue({
        workExperience: workDetails.length > 0,
        internshipExperience: intershipDetails.length > 0,
        jobTitle: workDetails[0]?.title,
        workCompany: workDetails[0]?.institutionName,
      });
      if (intershipDetails?.length) {
        const primaryIntership = {
          internshipName: intershipDetails[0].title,
          internshipCompany: intershipDetails[0].institutionName,
          internshipStartDate: [
            dayjs(intershipDetails[0].startDate, "YYYY"),
            dayjs(intershipDetails[0].endDate, "YYYY"),
          ],
          internshipId: intershipDetails[0].id,
        };
        const internships = intershipDetails?.splice(1).map((x, index) => ({
          internshipName: x.title,
          internshipCompany: x.institutionName,
          internshipStartDate: [dayjs(x.startDate, "YYYY"), dayjs(x.endDate, "YYYY")],
          [`internship${index}`]: x.id,
        }));
        form.setFieldsValue({
          ...primaryIntership,
          internships: internships,
        });
      }
      if (workDetails?.length) {
        const primaryWorkDetails = {
          jobTitle: workDetails[0].title,
          workCompany: workDetails[0].institutionName,
          workStartDate: [
            dayjs(workDetails[0].startDate, "YYYY"),
            dayjs(workDetails[0].endDate, "YYYY"),
          ],
          workId: workDetails[0].id,
        };
        const works = workDetails?.splice(1).map((x, index) => ({
          jobTitle: x.title,
          workCompany: x.institutionName,
          workStartDate: [dayjs(x.startDate, "YYYY"), dayjs(x.endDate, "YYYY")],
          [`work${index}`]: x.id,
        }));
        form.setFieldsValue({
          ...primaryWorkDetails,
          works: works,
        });
      }
    });
  };

  const disableCurrentAndFutureYears = (current) => {
    return current && current.year() > dayjs().year();
  };

  useEffect(() => {
    fetchData();
  }, [isFormDisabled]);

  useUnsavedChanges(!isFormDisabled);

  return (
    <Fragment>
      {element}
      <div className="advance-carrier-information-page">
        <Container>
          <CardComponent>
            <Row>
              <Col xxl={24}>
                <div className="heading">
                  <BackWithLabel
                    label={`${isFormDisabled ? "" : "Edit "} Career and Education`}
                    path="/profile"
                  />
                </div>
              </Col>
            </Row>

            <Form
              form={form}
              name="advanceCarrierForm"
              initialValues={{ remember: true }}
              layout="vertical"
              requiredMark={customizeRequiredMark}
              onFinish={handleFinish}
            >
              {/* Education Form Start */}
              <Row gutter={[16, 4]} className={isFormDisabled ? "" : "sideborder"}>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item name="educationId" hidden>
                    <Input type="hidden" />
                  </Form.Item>
                  <Form.Item
                    label="Discipline"
                    name="disciplineId"
                    rules={[
                      {
                        required: true,
                        message: "Please select discipline!",
                      },
                    ]}
                  >
                    <Select
                      virtual={false}
                      // mode="multiple"
                      showSearch
                      maxTagCount={2}
                      placeholder="Select discipline"
                      className="w-100 multiSelect"
                      tagRender={tagRender}
                      options={disciplineOption}
                      disabled={isFormDisabled}
                      filterOption={filterOption}
                    />
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="Course Name"
                    name="courseName"
                    rules={[
                      {
                        required: false,
                        message: "Please enter your course name!",
                      },
                    ]}
                  >
                    <Input
                      disabled={isFormDisabled}
                      placeholder="Enter your course name"
                      onKeyPress={(e) => {
                        const charCode = e.which || e.keyCode;
                        if (!/[A-Za-z0-9\-()\s]/.test(String.fromCharCode(charCode))) {
                          e.preventDefault();
                        }
                      }}
                      onPaste={(e) => {
                        const pasteData = e.clipboardData.getData("text");
                        if (/[^A-Za-z0-9\-()\s]/.test(pasteData)) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="Country"
                    name="countryCode"
                    rules={[
                      {
                        required: true,
                        message: "Please select country!",
                      },
                    ]}
                  >
                    <Select
                      virtual={false}
                      showSearch
                      placeholder="Select country"
                      className="w-100"
                      onChange={(value) => handleCountryOnChange(0, value)}
                      filterOption={filterOption}
                      disabled={isFormDisabled}
                    >
                      {countryList.map((country) => {
                        return (
                          <Option key={country.name} value={country.isoCode} label={country.name}>
                            <Flex justify="start" align="center" gap={"5px"}>
                              <img src={country.flag} height={30} width={30} />
                              <span>{country.name}</span>
                            </Flex>
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="Region"
                    name="regionId"
                    rules={[
                      {
                        required: true,
                        message: "Please select region!",
                      },
                    ]}
                  >
                    <Select
                      virtual={false}
                      // mode="multiple"
                      showSearch
                      maxTagCount={2}
                      placeholder="Select region"
                      className="w-100 multiSelect"
                      tagRender={tagRender}
                      options={regionOptions[0] ?? []}
                      onChange={(value) => handleRegionOnChange(0, value)}
                      disabled={isFormDisabled}
                      filterOption={filterOption}
                    />
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="School/College Location"
                    name="location"
                    rules={[
                      {
                        required: false,
                        message: "Please enter your school/college locations!",
                      },
                    ]}
                  >
                    <Input
                      disabled={isFormDisabled}
                      placeholder="Enter your school or college location"
                      onKeyPress={(e) => {
                        const charCode = e.which || e.keyCode;
                        if (!/[A-Za-z0-9,\-\s()]/.test(String.fromCharCode(charCode))) {
                          e.preventDefault();
                        }
                      }}
                      onPaste={(e) => {
                        const pasteData = e.clipboardData.getData("text");
                        if (![...pasteData].every((char) => /[A-Za-z0-9,\-\s()]/.test(char))) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="Level of Study"
                    name="levelId"
                    rules={[
                      {
                        required: true,
                        message: "Please select your level of study!",
                      },
                    ]}
                  >
                    <Select
                      virtual={false}
                      showSearch
                      placeholder="Select your level of study"
                      className="w-100"
                      filterOption={filterOption}
                      disabled={isFormDisabled}
                    >
                      {levelOptions.map((level) => {
                        return (
                          <Option key={level.value} value={level.value} label={level.label}>
                            <div>{level.label}</div>
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="University"
                    name="universityId"
                    rules={[
                      {
                        required: true,
                        message: "Please select university!",
                      },
                    ]}
                  >
                    <Select
                      virtual={false}
                      // mode="multiple"
                      showSearch
                      maxTagCount={2}
                      placeholder="Select a University"
                      className="w-100 multiSelect"
                      tagRender={tagRender}
                      options={universityOptions[0] ?? []}
                      disabled={isFormDisabled}
                      filterOption={filterOption}
                    />
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="School/College Name"
                    name="institutionName"
                    rules={[
                      {
                        required: false,
                        message: "Please enter your school/college name!",
                      },
                    ]}
                  >
                    <Input
                      disabled={isFormDisabled}
                      placeholder="Enter your school or college name"
                      onKeyPress={(e) => {
                        const charCode = e.which || e.keyCode;
                        if (!/[A-Za-z0-9,\-\s()]/.test(String.fromCharCode(charCode))) {
                          e.preventDefault();
                        }
                      }}
                      onPaste={(e) => {
                        const pasteData = e.clipboardData.getData("text");
                        if (![...pasteData].every((char) => /[A-Za-z0-9,\-\s()]/.test(char))) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="Start Date"
                    name="startDate"
                    rules={[
                      {
                        required: true,
                        message: "Please select start date!",
                      },
                    ]}
                  >
                    <DatePicker
                      disabled={isFormDisabled}
                      picker="year"
                      placeholder="Select your start date"
                      disabledDate={disableCurrentAndFutureYears}
                    />
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="End Date"
                    name="endDate"
                    rules={[
                      {
                        required: true,
                        message: "Please select end date!",
                      },
                    ]}
                  >
                    <DatePicker
                      disabled={isFormDisabled}
                      picker="year"
                      placeholder="Select end date"
                      disabledDate={disableCurrentAndFutureYears}
                    />
                  </Form.Item>
                </Col>

                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="Result (in % or grade)"
                    name="resultGrade"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your grade!",
                      },
                    ]}
                  >
                    <Input
                      disabled={isFormDisabled}
                      placeholder="Enter your study grade"
                      onKeyPress={(e) => {
                        const charCode = e.which || e.keyCode;
                        if (!/[0-9.]/.test(String.fromCharCode(charCode))) {
                          e.preventDefault();
                        }
                      }}
                      onPaste={(e) => {
                        const pasteData = e.clipboardData.getData("text");
                        if (/[^0-9.]/.test(pasteData)) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                </Col>

                <Form.List name="educations">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.length === 0 && (
                        <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                          <div className="another-education">
                            <ButtonComponent
                              onClick={() => add()}
                              iconRight={false}
                              disabled={isFormDisabled}
                              label="Add Another Education"
                              style={{
                                width: "100%",
                                height: "2.5rem",
                                padding: "0.5rem 0",
                                border: `1px solid ${isFormDisabled ? "#6d6e75" : "#DB5800"}`,
                              }}
                            >
                              <IconAdd
                                width="24px"
                                height="24px"
                                color={isFormDisabled ? "#6d6e75" : "#DB5800"}
                                style={{
                                  border: `1px solid ${isFormDisabled ? "#6d6e75" : "#DB5800"}`,
                                  borderRadius: "4px",
                                }}
                              />
                            </ButtonComponent>
                          </div>
                        </Col>
                      )}
                      {fields.map(({ key, name, ...restField }, index) => (
                        <div
                          key={key}
                          className="another-education-form"
                          data-index={index}
                          style={{
                            // add border
                            // ...(index !== 0 && {
                            borderTop: "1px solid #eee",
                            paddingTop: "1rem",
                            // }),
                          }}
                        >
                          <Row gutter={[16, 4]}>
                            <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                              <Form.Item name={`educationId${index}`} hidden>
                                <Input type="hidden" />
                              </Form.Item>
                              <Form.Item
                                {...restField}
                                name={[name, "disciplineId"]}
                                label="Discipline"
                                rules={[
                                  {
                                    required: true,
                                    message: "Please select discipline!",
                                  },
                                ]}
                              >
                                <Select
                                  virtual={false}
                                  // mode="multiple"
                                  maxTagCount={2}
                                  placeholder="Select discipline"
                                  className="w-100 multiSelect"
                                  tagRender={tagRender}
                                  options={disciplineOption}
                                  disabled={isFormDisabled}
                                />
                              </Form.Item>
                            </Col>
                            <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                              <Form.Item
                                {...restField}
                                name={[name, "courseName"]}
                                label="Course Name"
                                rules={[
                                  {
                                    required: true,
                                    message: "Please enter Course",
                                  },
                                ]}
                              >
                                <Input
                                  disabled={isFormDisabled}
                                  placeholder="Enter your course name"
                                  onKeyPress={(e) => {
                                    const charCode = e.which || e.keyCode;
                                    if (!/[A-Za-z0-9\-()\s]/.test(String.fromCharCode(charCode))) {
                                      e.preventDefault();
                                    }
                                  }}
                                  onPaste={(e) => {
                                    const pasteData = e.clipboardData.getData("text");
                                    if (/[^A-Za-z0-9\-()\s]/.test(pasteData)) {
                                      e.preventDefault();
                                    }
                                  }}
                                />
                              </Form.Item>
                            </Col>
                            <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                              <Form.Item
                                {...restField}
                                name={[name, "countryCode"]}
                                label="Country"
                                rules={[
                                  {
                                    required: true,
                                    message: "Please select country!",
                                  },
                                ]}
                              >
                                <Select
                                  virtual={false}
                                  showSearch
                                  placeholder="Select country"
                                  className="w-100"
                                  onChange={(value) => handleCountryOnChange(index, value)}
                                  filterOption={filterOption}
                                  disabled={isFormDisabled}
                                >
                                  {countryList.map((country) => {
                                    return (
                                      <Option
                                        key={country.name}
                                        value={country.isoCode}
                                        label={country.name}
                                      >
                                        <Flex justify="start" align="center" gap={"5px"}>
                                          <img
                                            src={country.flag}
                                            height={30}
                                            width={30}
                                            className={isFormDisabled && "disabled-image"}
                                          />
                                          <span>{country.name}</span>
                                        </Flex>
                                      </Option>
                                    );
                                  })}
                                </Select>
                              </Form.Item>
                            </Col>
                            <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                              <Form.Item
                                {...restField}
                                name={[name, "regionId"]}
                                label="Region"
                                rules={[
                                  {
                                    required: true,
                                    message: "Please select region!",
                                  },
                                ]}
                              >
                                <Select
                                  virtual={false}
                                  // mode="multiple"
                                  maxTagCount={2}
                                  placeholder="Select region"
                                  className="w-100 multiSelect"
                                  tagRender={tagRender}
                                  options={regionOptions[index] ?? []}
                                  onChange={(value) => handleRegionOnChange(index, value)}
                                  disabled={isFormDisabled}
                                />
                              </Form.Item>
                            </Col>
                            <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                              <Form.Item
                                {...restField}
                                name={[name, "location"]}
                                label="School/College Location"
                                rules={[
                                  {
                                    required: true,
                                    message: "Please enter school/college location!",
                                  },
                                ]}
                              >
                                <Input
                                  disabled={isFormDisabled}
                                  placeholder="Enter your school or college location"
                                  onKeyPress={(e) => {
                                    const charCode = e.which || e.keyCode;
                                    if (!/[A-Za-z0-9,\-\s()]/.test(String.fromCharCode(charCode))) {
                                      e.preventDefault();
                                    }
                                  }}
                                  onPaste={(e) => {
                                    const pasteData = e.clipboardData.getData("text");
                                    if (
                                      ![...pasteData].every((char) =>
                                        /[A-Za-z0-9,\-\s()]/.test(char)
                                      )
                                    ) {
                                      e.preventDefault();
                                    }
                                  }}
                                />
                              </Form.Item>
                            </Col>
                            <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                              <Form.Item
                                {...restField}
                                name={[name, "levelId"]}
                                label="Level of Study"
                                rules={[
                                  {
                                    required: true,
                                    message: "Please select your level of study!",
                                  },
                                ]}
                              >
                                <Select
                                  virtual={false}
                                  showSearch
                                  placeholder="Select your level of study"
                                  className="w-100"
                                  filterOption={filterOption}
                                  disabled={isFormDisabled}
                                >
                                  {levelOptions.map((level) => {
                                    return (
                                      <Option
                                        key={level.value}
                                        value={level.value}
                                        label={level.label}
                                      >
                                        <div>{level.label}</div>
                                      </Option>
                                    );
                                  })}
                                </Select>
                              </Form.Item>
                            </Col>
                            <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                              <Form.Item
                                {...restField}
                                name={[name, "universityId"]}
                                label="University"
                                rules={[
                                  {
                                    required: true,
                                    message: "Please select university!",
                                  },
                                ]}
                              >
                                <Select
                                  virtual={false}
                                  // mode="multiple"
                                  maxTagCount={2}
                                  placeholder="Select a University"
                                  className="w-100 multiSelect"
                                  tagRender={tagRender}
                                  options={universityOptions[index] ?? []}
                                  disabled={isFormDisabled}
                                />
                              </Form.Item>
                            </Col>
                            <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                              <Form.Item
                                {...restField}
                                name={[name, "institutionName"]}
                                label="School/College Name"
                                rules={[
                                  {
                                    required: true,
                                    message: "Please enter school/college name!",
                                  },
                                ]}
                              >
                                <Input
                                  disabled={isFormDisabled}
                                  placeholder="Enter your school or college"
                                  onKeyPress={(e) => {
                                    const charCode = e.which || e.keyCode;
                                    if (!/[A-Za-z0-9,\-\s()]/.test(String.fromCharCode(charCode))) {
                                      e.preventDefault();
                                    }
                                  }}
                                  onPaste={(e) => {
                                    const pasteData = e.clipboardData.getData("text");
                                    if (
                                      ![...pasteData].every((char) =>
                                        /[A-Za-z0-9,\-\s()]/.test(char)
                                      )
                                    ) {
                                      e.preventDefault();
                                    }
                                  }}
                                />
                              </Form.Item>
                            </Col>
                            <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                              <Form.Item
                                {...restField}
                                name={[name, "startDate"]}
                                label="Start Date"
                                rules={[
                                  {
                                    required: true,
                                    message: "Please select start date!",
                                  },
                                ]}
                              >
                                <DatePicker
                                  disabled={isFormDisabled}
                                  picker="year"
                                  placeholder="Select your start date"
                                  disabledDate={disableCurrentAndFutureYears}
                                />
                              </Form.Item>
                            </Col>
                            <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                              <Form.Item
                                {...restField}
                                name={[name, "endDate"]}
                                label="End Date"
                                rules={[
                                  {
                                    required: true,
                                    message: "Please select end date!",
                                  },
                                ]}
                              >
                                <DatePicker
                                  disabled={isFormDisabled}
                                  picker="year"
                                  placeholder="Select end date"
                                  disabledDate={disableCurrentAndFutureYears}
                                />
                              </Form.Item>
                            </Col>

                            <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                              <Form.Item
                                label="Result (in % or grade)"
                                {...restField}
                                name={[name, "resultGrade"]}
                                rules={[
                                  {
                                    required: true,
                                    message: "Please enter your grade!",
                                  },
                                ]}
                              >
                                <Input
                                  disabled={isFormDisabled}
                                  placeholder="Enter your study grade"
                                  onKeyPress={(e) => {
                                    const charCode = e.which || e.keyCode;
                                    if (!/[0-9.]/.test(String.fromCharCode(charCode))) {
                                      e.preventDefault();
                                    }
                                  }}
                                  onPaste={(e) => {
                                    const pasteData = e.clipboardData.getData("text");
                                    if (/[^0-9.]/.test(pasteData)) {
                                      e.preventDefault();
                                    }
                                  }}
                                />
                              </Form.Item>
                            </Col>
                            {index === fields.length - 1 && (
                              <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                                <div className="another-education">
                                  <ButtonComponent
                                    onClick={() => add()}
                                    iconRight={false}
                                    disabled={isFormDisabled}
                                    label="Add Another Education"
                                    style={{
                                      width: "100%",
                                      height: "2.5rem",
                                      padding: "0.5rem 0",
                                      border: `1px solid ${isFormDisabled ? "#6d6e75" : "#DB5800"}`,
                                    }}
                                  >
                                    <IconAdd
                                      width="24px"
                                      height="24px"
                                      color={isFormDisabled ? "#6d6e75" : "#DB5800"}
                                      style={{
                                        border: `1px solid ${
                                          isFormDisabled ? "#6d6e75" : "#DB5800"
                                        }`,
                                        borderRadius: "4px",
                                      }}
                                    />
                                  </ButtonComponent>
                                </div>
                              </Col>
                            )}

                            <Col
                              xxl={8}
                              xl={8}
                              lg={12}
                              md={12}
                              sm={24}
                              xs={24}
                              className="delete-form"
                            >
                              {fields.length > 0 ? (
                                <IconDelete
                                  className="dynamic-delete-button"
                                  onClick={!isFormDisabled ? () => remove(name) : null}
                                  color={isFormDisabled ? "#6d6e75" : "#E9635E"}
                                  style={{
                                    border: "1px solid",
                                    borderRadius: "4px",
                                    padding: "4px",
                                    cursor: isFormDisabled ? "" : "pointer",
                                  }}
                                />
                              ) : null}
                            </Col>
                          </Row>
                        </div>
                      ))}
                    </>
                  )}
                </Form.List>
              </Row>
              {/* Education Form End */}
              <Divider />

              <Row justify="start">
                <Col xxl={24}>
                  <div className="heading">
                    <div className="heading-text body-b1">Add Internship Details</div>
                  </div>
                </Col>
              </Row>

              <Row gutter={[16, 0]} justify="start" className={isFormDisabled ? "" : "sideborder"}>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item label="Any internship experience?" name="internshipExperience">
                    <Select
                      virtual={false}
                      showSearch
                      placeholder="Select an option"
                      className="w-100"
                      filterOption={filterOption}
                      disabled={isFormDisabled}
                      onChange={(value) =>
                        setFormData((prevState) => ({
                          ...prevState,
                          internshipExperience: value,
                        }))
                      }
                    >
                      {bullionOptions.map((bullion) => {
                        return (
                          <Option key={bullion.value} value={bullion.value} label={bullion.label}>
                            {bullion.label}
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
                {formData.internshipExperience && (
                  <>
                    <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                      <Form.Item name="internshipId" hidden>
                        <Input type="hidden" />
                      </Form.Item>
                      <Form.Item
                        label="Internship Name"
                        name="internshipName"
                        rules={[
                          {
                            required: true,
                            message: "Please enter Internship name",
                          },
                        ]}
                      >
                        <Input
                          disabled={isFormDisabled}
                          placeholder="Enter your internship name"
                          onKeyPress={(e) => {
                            const charCode = e.which || e.keyCode;
                            if (!/[A-Za-z0-9\s]/.test(String.fromCharCode(charCode))) {
                              e.preventDefault();
                            }
                          }}
                          onPaste={(e) => {
                            const pasteData = e.clipboardData.getData("text");
                            if (/[^A-Za-z0-9\s]/.test(pasteData)) {
                              e.preventDefault();
                            }
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                      <Form.Item
                        label="Internship Company/Institution Name"
                        name="internshipCompany"
                        rules={[
                          {
                            required: true,
                            message: "Please enter Internship Company",
                          },
                        ]}
                      >
                        <Input
                          disabled={isFormDisabled}
                          placeholder="Enter your company institute name"
                          onKeyPress={(e) => {
                            const charCode = e.which || e.keyCode;
                            if (!/[A-Za-z0-9\s]/.test(String.fromCharCode(charCode))) {
                              e.preventDefault();
                            }
                          }}
                          onPaste={(e) => {
                            const pasteData = e.clipboardData.getData("text");
                            if (/[^A-Za-z0-9\s]/.test(pasteData)) {
                              e.preventDefault();
                            }
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                      <Form.Item
                        label="Internship Start/End Date"
                        name="internshipStartDate"
                        rules={[
                          {
                            required: true,
                            message: "Please enter Internship Dates",
                          },
                        ]}
                      >
                        <RangePicker
                          picker="year"
                          disabled={isFormDisabled}
                          disabledDate={disableCurrentAndFutureYears}
                        />
                      </Form.Item>
                    </Col>
                  </>
                )}
              </Row>

              {formData.internshipExperience && (
                <Form.List name="internships">
                  {(fields, { add, remove }, { errors }) => (
                    <>
                      {fields.length === 0 && (
                        <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                          <div className="another-internship">
                            <ButtonComponent
                              onClick={() => add()}
                              iconRight={false}
                              disabled={isFormDisabled}
                              label="Add Another Internship"
                              style={{
                                width: "100%",
                                height: "2.5rem",
                                padding: "0.5rem 0",
                                border: `1px solid ${isFormDisabled ? "#6d6e75" : "#DB5800"}`,
                              }}
                            >
                              <IconAdd
                                width="24px"
                                height="24px"
                                color={isFormDisabled ? "#6d6e75" : "#DB5800"}
                                style={{
                                  border: `1px solid ${isFormDisabled ? "#6d6e75" : "#DB5800"}`,
                                  borderRadius: "4px",
                                }}
                              />
                            </ButtonComponent>
                            <Form.ErrorList errors={errors} />
                          </div>
                        </Col>
                      )}
                      {fields.map((field, index) => (
                        <div className="another-internship-form" key={field.key}>
                          <Form.Item
                            {...(index === 0 ? formItemLayout : formItemLayoutWithOutLabel)}
                            key={field.key}
                          >
                            <div className="dynamic-input">
                              <Row gutter={[16, 0]} justify="start">
                                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                                  <Form.Item name={`internship${index}`} hidden>
                                    <Input type="hidden" />
                                  </Form.Item>
                                  <Form.Item
                                    label="Internship Name"
                                    name={[field.name, "internshipName"]}
                                    rules={[
                                      {
                                        required: true,
                                        message: "Please enter Internship name",
                                      },
                                    ]}
                                  >
                                    <Input
                                      disabled={isFormDisabled}
                                      placeholder="Enter your internship name"
                                      onKeyPress={(e) => {
                                        const charCode = e.which || e.keyCode;
                                        if (!/[A-Za-z0-9\s]/.test(String.fromCharCode(charCode))) {
                                          e.preventDefault();
                                        }
                                      }}
                                      onPaste={(e) => {
                                        const pasteData = e.clipboardData.getData("text");
                                        if (/[^A-Za-z0-9\s]/.test(pasteData)) {
                                          e.preventDefault();
                                        }
                                      }}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                                  <Form.Item
                                    label="Internship Company/Institution Name"
                                    name={[field.name, "internshipCompany"]}
                                    rules={[
                                      {
                                        required: true,
                                        message: "Please enter Internship Company",
                                      },
                                    ]}
                                  >
                                    <Input
                                      disabled={isFormDisabled}
                                      placeholder="Enter your company institute name"
                                      onKeyPress={(e) => {
                                        const charCode = e.which || e.keyCode;
                                        if (!/[A-Za-z0-9\s]/.test(String.fromCharCode(charCode))) {
                                          e.preventDefault();
                                        }
                                      }}
                                      onPaste={(e) => {
                                        const pasteData = e.clipboardData.getData("text");
                                        if (/[^A-Za-z0-9\s]/.test(pasteData)) {
                                          e.preventDefault();
                                        }
                                      }}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                                  <Form.Item
                                    label="Internship Start/End Date"
                                    name={[field.name, "internshipStartDate"]}
                                    rules={[
                                      {
                                        required: true,
                                        message: "Please enter Internship Dates",
                                      },
                                    ]}
                                  >
                                    <RangePicker
                                      picker="year"
                                      disabled={isFormDisabled}
                                      disabledDate={disableCurrentAndFutureYears}
                                    />
                                  </Form.Item>
                                </Col>
                                {index === fields.length - 1 && (
                                  <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                                    <div className="another-internship">
                                      <ButtonComponent
                                        onClick={() => add()}
                                        iconRight={false}
                                        disabled={isFormDisabled}
                                        label="Add Another Internship"
                                        style={{
                                          width: "100%",
                                          height: "2.5rem",
                                          padding: "0.5rem 0",
                                          border: `1px solid ${
                                            isFormDisabled ? "#6d6e75" : "#DB5800"
                                          }`,
                                        }}
                                      >
                                        <IconAdd
                                          width="24px"
                                          height="24px"
                                          color={isFormDisabled ? "#6d6e75" : "#DB5800"}
                                          style={{
                                            border: `1px solid ${
                                              isFormDisabled ? "#6d6e75" : "#DB5800"
                                            }`,
                                            borderRadius: "4px",
                                          }}
                                        />
                                      </ButtonComponent>
                                      <Form.ErrorList errors={errors} />
                                    </div>
                                  </Col>
                                )}
                                <Col
                                  xxl={8}
                                  xl={8}
                                  lg={12}
                                  md={12}
                                  sm={24}
                                  xs={24}
                                  className="delete-form"
                                >
                                  {fields.length > 0 ? (
                                    <IconDelete
                                      className="dynamic-delete-button"
                                      onClick={!isFormDisabled ? () => remove(field.name) : null}
                                      color={isFormDisabled ? "#6d6e75" : "#E9635E"}
                                      style={{
                                        border: "1px solid",
                                        borderRadius: "4px",
                                        padding: "4px",
                                        cursor: `${isFormDisabled ? "" : "pointer"}`,
                                      }}
                                    />
                                  ) : null}
                                </Col>
                              </Row>
                            </div>
                          </Form.Item>
                        </div>
                      ))}
                    </>
                  )}
                </Form.List>
              )}

              <Divider />

              <Row justify="start">
                <Col xxl={24}>
                  <div className="heading">
                    <div className="heading-text body-b1">Add Work Details</div>
                  </div>
                </Col>
              </Row>

              <Row gutter={[16, 0]} justify="start" className={isFormDisabled ? "" : "sideborder"}>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item label="Any Work experience?" name="workExperience">
                    <Select
                      virtual={false}
                      showSearch
                      placeholder="Select an option"
                      className="w-100"
                      filterOption={filterOption}
                      disabled={isFormDisabled}
                      onChange={(value) =>
                        setFormData((prevState) => ({
                          ...prevState,
                          workExperience: value,
                        }))
                      }
                    >
                      {bullionOptions.map((bullion) => {
                        return (
                          <Option key={bullion.value} value={bullion.value} label={bullion.label}>
                            {bullion.label}
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
                {formData.workExperience && (
                  <>
                    <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                      <Form.Item name="workId" hidden>
                        <Input type="hidden" />
                      </Form.Item>
                      <Form.Item
                        label="Job Title"
                        name="jobTitle"
                        rules={[
                          {
                            required: true,
                            message: "Please enter Job Title",
                          },
                        ]}
                      >
                        <Input
                          disabled={isFormDisabled}
                          placeholder="Enter your job title"
                          onKeyPress={(e) => {
                            const charCode = e.which || e.keyCode;
                            if (!/[A-Za-z0-9\s]/.test(String.fromCharCode(charCode))) {
                              e.preventDefault();
                            }
                          }}
                          onPaste={(e) => {
                            const pasteData = e.clipboardData.getData("text");
                            if (/[^A-Za-z0-9\s]/.test(pasteData)) {
                              e.preventDefault();
                            }
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                      <Form.Item
                        label="Company/Institution Name"
                        name="workCompany"
                        rules={[
                          {
                            required: true,
                            message: "Please enter Institute name",
                          },
                        ]}
                      >
                        <Input
                          disabled={isFormDisabled}
                          placeholder="Enter your company institute name"
                          onKeyPress={(e) => {
                            const charCode = e.which || e.keyCode;
                            if (!/[A-Za-z0-9\s]/.test(String.fromCharCode(charCode))) {
                              e.preventDefault();
                            }
                          }}
                          onPaste={(e) => {
                            const pasteData = e.clipboardData.getData("text");
                            if (/[^A-Za-z0-9\s]/.test(pasteData)) {
                              e.preventDefault();
                            }
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                      <Form.Item
                        label="Work Start/End Date"
                        name="workStartDate"
                        rules={[
                          {
                            required: true,
                            message: "Please enter work dates",
                          },
                        ]}
                      >
                        <RangePicker
                          picker="year"
                          disabled={isFormDisabled}
                          disabledDate={disableCurrentAndFutureYears}
                        />
                      </Form.Item>
                    </Col>
                  </>
                )}
              </Row>

              {formData.workExperience && (
                <Form.List name="works">
                  {(fields, { add, remove }, { errors }) => (
                    <>
                      {fields.length === 0 && (
                        <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                          <div className="another-education">
                            <ButtonComponent
                              onClick={() => add()}
                              iconRight={false}
                              disabled={isFormDisabled}
                              label="Add Another Work"
                              style={{
                                width: "100%",
                                height: "2.5rem",
                                padding: "0.5rem 0",
                                border: `1px solid ${isFormDisabled ? "#6d6e75" : "#DB5800"}`,
                              }}
                            >
                              <IconAdd
                                width="24px"
                                height="24px"
                                color={isFormDisabled ? "#6d6e75" : "#DB5800"}
                                style={{
                                  border: `1px solid ${isFormDisabled ? "#6d6e75" : "#DB5800"}`,
                                  borderRadius: "4px",
                                }}
                              />
                            </ButtonComponent>
                            <Form.ErrorList errors={errors} />
                          </div>
                        </Col>
                      )}
                      {fields.map((field, index) => (
                        <div className="another-work-form" key={field.key}>
                          <Form.Item
                            {...(index === 0 ? formItemLayout : formItemLayoutWithOutLabel)}
                            key={field.key}
                          >
                            <div className="dynamic-input">
                              <Row gutter={[16, 0]} justify="start">
                                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                                  <Form.Item name={`work${index}`} hidden>
                                    <Input type="hidden" />
                                  </Form.Item>
                                  <Form.Item
                                    label="Job Title"
                                    name={[field.name, "jobTitle"]}
                                    rules={[
                                      {
                                        required: true,
                                        message: "Please enter Job Title",
                                      },
                                    ]}
                                  >
                                    <Input
                                      disabled={isFormDisabled}
                                      placeholder="Enter your job title"
                                      onKeyPress={(e) => {
                                        const charCode = e.which || e.keyCode;
                                        if (!/[A-Za-z0-9\s]/.test(String.fromCharCode(charCode))) {
                                          e.preventDefault();
                                        }
                                      }}
                                      onPaste={(e) => {
                                        const pasteData = e.clipboardData.getData("text");
                                        if (/[^A-Za-z0-9\s]/.test(pasteData)) {
                                          e.preventDefault();
                                        }
                                      }}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                                  <Form.Item
                                    label="Company/Institution Name"
                                    name={[field.name, "workCompany"]}
                                    rules={[
                                      {
                                        required: true,
                                        message: "Please enter Institute name",
                                      },
                                    ]}
                                  >
                                    <Input
                                      disabled={isFormDisabled}
                                      placeholder="Enter your company institute name"
                                      onKeyPress={(e) => {
                                        const charCode = e.which || e.keyCode;
                                        if (!/[A-Za-z0-9\s]/.test(String.fromCharCode(charCode))) {
                                          e.preventDefault();
                                        }
                                      }}
                                      onPaste={(e) => {
                                        const pasteData = e.clipboardData.getData("text");
                                        if (/[^A-Za-z0-9\s]/.test(pasteData)) {
                                          e.preventDefault();
                                        }
                                      }}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                                  <Form.Item
                                    label="Work Start/End Date"
                                    name={[field.name, "workStartDate"]}
                                    rules={[
                                      {
                                        required: true,
                                        message: "Please enter work dates",
                                      },
                                    ]}
                                  >
                                    <RangePicker
                                      picker="year"
                                      disabled={isFormDisabled}
                                      disabledDate={disableCurrentAndFutureYears}
                                    />
                                  </Form.Item>
                                </Col>
                                {index === fields.length - 1 && (
                                  <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                                    <div className="another-education">
                                      <ButtonComponent
                                        onClick={() => add()}
                                        iconRight={false}
                                        disabled={isFormDisabled}
                                        label="Add Another Work"
                                        style={{
                                          width: "100%",
                                          height: "2.5rem",
                                          padding: "0.5rem 0",
                                          border: `1px solid ${
                                            isFormDisabled ? "#6d6e75" : "#DB5800"
                                          }`,
                                        }}
                                      >
                                        <IconAdd
                                          width="24px"
                                          height="24px"
                                          color={isFormDisabled ? "#6d6e75" : "#DB5800"}
                                          style={{
                                            border: `1px solid ${
                                              isFormDisabled ? "#6d6e75" : "#DB5800"
                                            }`,
                                            borderRadius: "4px",
                                          }}
                                        />
                                      </ButtonComponent>
                                      <Form.ErrorList errors={errors} />
                                    </div>
                                  </Col>
                                )}
                                <Col
                                  xxl={8}
                                  xl={8}
                                  lg={12}
                                  md={12}
                                  sm={24}
                                  xs={24}
                                  className="delete-form"
                                >
                                  {fields.length > 0 ? (
                                    <IconDelete
                                      className="dynamic-delete-button"
                                      onClick={!isFormDisabled ? () => remove(field.name) : null}
                                      color={isFormDisabled ? "#6d6e75" : "#E9635E"}
                                      style={{
                                        border: "1px solid",
                                        borderRadius: "4px",
                                        padding: "4px",
                                        cursor: `${isFormDisabled ? "" : "pointer"}`,
                                      }}
                                    />
                                  ) : null}
                                </Col>
                              </Row>
                            </div>
                          </Form.Item>
                        </div>
                      ))}
                    </>
                  )}
                </Form.List>
              )}

              <Divider />

              {!isFormDisabled && (
                <Row justify="center">
                  <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                    <div className="submit-button">
                      <ButtonComponent
                        label="Save Career and Education"
                        varient="primary"
                        htmlType="submit"
                        className="w-100"
                      />
                    </div>
                  </Col>
                </Row>
              )}
            </Form>
            {isFormDisabled && (
              <Row justify="center">
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <div className="submit-button">
                    <ButtonComponent
                      label="Edit Career and Education"
                      varient="primary"
                      htmlType="button"
                      onClick={() => {
                        setIsFormDisabled(false);
                      }}
                      className="w-100"
                    />
                  </div>
                </Col>
              </Row>
            )}
          </CardComponent>
        </Container>
      </div>
    </Fragment>
  );
};

export default AdvanceCarrierInformation;
